import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { DECK_SLIDES } from "./PitchDeck";
import Logo from "../components/Logo";

/**
 * /pitch/pdf — print-ready view of the pitch deck. One 16:9 landscape page per
 * slide; the browser's "Save as PDF" produces a shareable file. `?auto=1`
 * opens the print dialog on load.
 */
export default function PitchDeckPrint() {
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("auto") === "1") {
      const t = setTimeout(() => window.print(), 600);
      return () => clearTimeout(t);
    }
  }, [params]);

  return (
    <div className="bg-slate-200 min-h-screen font-body text-slate-900 print:bg-white" data-testid="pitch-print">
      <div className="print:hidden sticky top-0 z-50 bg-white border-b-2 border-slate-900 px-6 py-3 flex items-center justify-between" data-testid="pitch-print-toolbar">
        <div className="flex items-center gap-4">
          <Link to="/pitch" className="inline-flex items-center gap-1 text-sm font-bold text-slate-700 hover:text-slate-900" data-testid="pitch-print-back">
            <ArrowLeft size={14} strokeWidth={2.5} /> Back to deck
          </Link>
          <Logo size={24} wordmark="compact" />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500 hidden sm:inline">Choose <b>Save as PDF</b> · Landscape · no margins for best results</span>
          <button onClick={() => window.print()} className="brut-btn text-sm" data-testid="pitch-print-button">
            <Printer size={14} strokeWidth={2.5} /> Save as PDF
          </button>
        </div>
      </div>

      <div className="flex flex-col items-center gap-6 py-8 print:gap-0 print:py-0">
        {DECK_SLIDES.map((Slide, i) => (
          <section
            key={Slide.name}
            className="deck-page relative bg-[#FDF8F5] border-2 border-slate-900 print:border-0 overflow-hidden"
            data-testid={`pitch-print-slide-${i + 1}`}
          >
            <div className="absolute inset-0 px-6 py-8 flex items-center justify-center">
              <Slide />
            </div>
            <div className="absolute bottom-3 right-5 text-[10px] font-mono text-slate-400 uppercase tracking-widest">
              AiMedley · {String(i + 1).padStart(2, "0")} / {String(DECK_SLIDES.length).padStart(2, "0")}
            </div>
          </section>
        ))}
      </div>

      <style>{`
        .deck-page { width: 1280px; height: 720px; }
        .deck-page .stagger > * { opacity: 1 !important; animation: none !important; }
        .deck-page .marquee { animation: none !important; }
        @page { size: 1280px 720px; margin: 0; }
        @media print {
          html, body { background: #fff !important; }
          .deck-page { page-break-after: always; break-after: page; margin: 0; }
          .deck-page:last-child { page-break-after: auto; break-after: auto; }
        }
      `}</style>
    </div>
  );
}
